<template>
  <div class="container py-5">

    <button
      :disabled="!valid || storedParts.length == 0 || saving" 
      class="button is-primary mb-4"
      :class="{ 'is-loading': saving }"
      @click="saveStorage()">
      Save Storage
    </button>

    <v-form :valid.sync="valid" class="columns is-multiline">
      
      <div class="column is-one-fifth">
        <label class="label">Project*</label>
        <v-select 
          v-if="projects.length > 0"
          required
          :options="projects"
          item-text="project_name"
          item-value="id"
          v-model="projectId"
        />
        <span class="help">Only projects with cut sheets are listed</span>
      </div>

      <div class="column is-one-fifth">
        <label class="label">Storage Name*</label>
        <v-text-field
          required
          placeholder="Storage Name"
          v-model="storage.storage_name">
        </v-text-field>
      </div>

      <div class="column is-one-fifth">
        <div class="label-and-button">
          <label class="label">Storage Type*</label>
          <span 
            class="tag" 
            @click="managingStorageTypes = true">
            <span>Manage Storage Types</span>
          </span>
        </div>
        <v-select
          v-if="storageTypes.length > 0"
          required
          item-text="storage_type_name"
          item-value="id"
          :options="storageTypes"
          v-model="storage.storage_type_id"
        />
      </div>

      <div class="column is-one-fifth">
        <label class="label">Warehouse*</label>
        <v-select
          required
          item-text="warehouse_name"
          item-value="id"
          :options="warehouses"
          v-model="warehouseId"
        />
      </div>

      <div class="column is-one-fifth">
        <label class="label">Warehouse Space*</label>
        <template v-if="warehouseId">
          <v-select
            required
            v-if="warehouse.locations.length > 0"
            item-text="location_name"
            item-value="id"
            :options="warehouse.locations"
            v-model="storage.warehouse_location_id"
          />
          <div v-else class="mt-2 has-text-grey-light">
            There are no spaces in this warehouse
          </div>
        </template>
        <div v-else class="has-text-grey-light mt-2">
          Please select a warehouse
        </div>
      </div>

      <!-- <div class="column is-one-third">
        <label class="label">Packing List Attachment</label>
        <v-file-upload
          has-name
          file-type-validator=".*"
          v-model="attachment">
        </v-file-upload>
      </div> -->

    </v-form>

    <hr class="my-4"/>

    <div class="columns">
      <div class="column is-half">

        <div class="menu-label">
          Project Parts
        </div>

        <v-fade-transition mode="out-in">
          <article 
            v-if="cutSheetParts.length == 0 && !gettingParts" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              Choose a project to see a list of parts
            </div>
          </article>

          <div 
            v-if="gettingParts" 
            class="is-relative">
            <v-spin-loader :value="true">
              <div class="py-6"></div>
            </v-spin-loader>
          </div>

          <table 
            class="table is-fullwidth has-small-font" 
            v-if="!gettingParts && cutSheetParts.length > 0">
            <thead>
              <tr>
                <th></th>
                <th 
                  v-for="(header, headerIndex) in partHeaders"
                  :key="headerIndex">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr    
                class="v-centered"           
                v-for="part in cutSheetParts"
                :key="part.id">
                <td>
                  <v-checkbox
                    :value="partsSelected.indexOf(part.id) != -1"
                    @input="togglePart(part.id)">
                  </v-checkbox>
                </td>
                <td>
                  <router-link :to="{ 
                    name: 'CutSheetViewer', 
                    params: { cutSheetId: part.cutsheet.id }}">
                    {{ part.cutsheet.cutsheet_no }}
                  </router-link>
                </td>
                <td>
                  {{ part.part_no }}
                </td>
                <td>
                  <div>
                    {{ part.output_product_master.name }}
                  </div>
                  <div class="help text--nowrap">
                    {{ lwt(part.output_stocking_unit) }}
                  </div>
                </td>
                <td>
                  {{ part.total_sqft | round(1) }}
                </td>
                <td>
                  {{ part.quantity | round(0) }}
                </td>
                <td>
                  {{ part.qty_stored | round(0) }}
                </td>
                <!-- <td>
                  <span v-if="part.skid">
                    {{ part.skid.skid_no }}
                  </span>
                </td> -->
              </tr>
            </tbody>
          </table>  
        </v-fade-transition>

      </div>

      <div class="column is-half">
        <div class="menu-label">Parts to be Stored</div>

        <v-fade-transition mode="out-in">
          <table 
            class="table is-fullwidth has-small-font" 
            v-if="storedParts.length > 0">
            <thead>
              <tr>
                <th 
                  v-for="(header, headerIndex) in partStoredHeaders"
                  :key="headerIndex">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr         
                class="v-centered"                 
                v-for="part in storedParts"
                :key="part.id">
                <td>
                  <router-link 
                    class="text--nowrap"
                    :to="{ 
                    name: 'CutSheetViewer', 
                    params: { cutSheetId: part.cutsheet.id }}">
                    {{ part.cutsheet.cutsheet_no }}
                  </router-link>
                </td>
                <td>
                  {{ part.part_no }}
                </td>
                <td>
                  <div>
                    {{ part.output_product_master.name }}
                  </div>
                  <div class="help text--nowrap">
                    {{ lwt(part.output_stocking_unit) }}
                  </div>
                </td>
                <td>
                  {{ part.total_sqft | round(1) }}
                </td>
                <td>
                  {{ part.quantity | round(0) }}
                </td>
                <td>
                  <div class="flex flex--row">
                    <v-text-field 
                      type="number"
                      placeholder="Quantity to be stored"
                      :rules="[v => +v <= +part.quantity ? true : 'Cannot exceed quantity available']"
                      v-model="part.qty_to_be_stored"/>
                    <button 
                      @click="part.qty_to_be_stored = part.quantity" 
                      class="button">
                      <span class="icon">
                        <i class="fas fa-angle-double-up"/>
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </v-fade-transition>
      </div>
    </div>

    <manage-storage-types-modal 
      v-model="managingStorageTypes"/>

    <v-loading 
      :value="gettingProjects" 
      message="Getting projects with cut sheets ..."/>

  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VForm from '@/components/base/VForm'
import VCheckbox from '@/components/base/VCheckbox'

import VSpinLoader from '@/components/base/VSpinLoader'
import VLoading from '@/components/base/VLoading'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import ManageStorageTypesModal from '@/components/warehousing/ManageStorageTypesModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VSelect, VTextField, VForm, VCheckbox, VSpinLoader, VLoading,
    VFadeTransition, ManageStorageTypesModal
  },
  data() {
    return {
      partHeaders: [
        'Cut Sheet', 'Part Number', 'Product', 'Total Sqft', 'Qty', 'Qty Already Stored'
      ],
      partStoredHeaders: [
        'Cut Sheet', 'Part Number', 'Product', 'Total Sqft', 'Qty', 'Qty to be Stored'
      ],
      projectId: null,
      storage: {
        storage_name: null,
        storage_type_id: null,
        warehouse_location_id: null
      },
      projects: [],
      cutSheetParts: [],
      partsSelected: [],
      valid: false,
      managingStorageTypes: false,
      warehouseId: null,
      gettingProjects: false,
      gettingParts: false,
      saving: false
    }
  },
  computed: {
    ...mapState("storages", ["storages"]),
    ...mapState("storageTypes", ["storageTypes"]),
    ...mapState("warehouses", ["warehouses"]),
    storedParts() {
      return this.cutSheetParts.filter(
        d => this.partsSelected.includes(d.id)
      );
    },
    warehouse() {
      return this.warehouses.filter(d => d.id == this.warehouseId)[0];
    }
  },
  watch: {
    projectId(id) {
      this.getCutSheetParts(id);
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("storages", ["getStorages", "addStorage", "addStorageUnit"]),
    ...mapActions("storageTypes", ["getStorageTypes"]),
    ...mapActions("warehouses", ["getWarehouses"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.storageTypes.length == 0) {
        this.getStorageTypes({ per_page: 100 });
      }

      if (this.warehouses.length == 0) {
        this.getWarehouses({ per_page: 100 });
      }

      this.gettingProjects = true;

      this.$http.projects.get(
        { has_cutsheets: true, per_page: 1000 }
      ).then(resp => {
        this.projects = resp.data.results;
        this.gettingProjects = false;
      }).catch(() => {
        this.gettingProjects = false;
      })
    },
    lwt(product) {
      if (product) {
        return `L${product.length_in_str}" x W${product.width_in_str}" x T${product.thick_in_str}"`
      }
    },
    saveStorage() {
      this.saving = true;
      this.addStorage(this.storage).then(resp => {
        this.saveStorageParts(resp.data.id);
      })
    },
    saveStorageParts(storageId) {
      let promises = [];
      this.storedParts.forEach(part => {
        promises.push(
          this.addStorageUnit({
            storage_id: storageId,
            stocking_unit_id: part.output_stocking_unit.id,
            qty: parseInt(part.qty_to_be_stored)
          })
        )
      })
      Promise.all(promises).then(() => {
        this.saving = false;
        this.setNotification({
          type: 'success',
          status: true,
          message: 'Storage successfully added'
        })
      }).catch(() => {
        this.saving = false;
      })
      this.$router.push({ name: 'StorageList' });
    },
    getCutSheetParts(projectId) {
      this.gettingParts = true;

      this.$http.cutsheetParts.get(
        { project_id: projectId, per_page: 1000 }
      ).then(resp => {
        resp.data.results.forEach(part => part.qty_to_be_stored = 0);
        this.cutSheetParts = resp.data.results;
        this.gettingParts = false;
      }).catch(err => this.gettingParts = false);
    },
    togglePart(id) {
      const index = this.partsSelected.indexOf(id);

      if (index == -1) {
        this.partsSelected.push(id);
      } else {
        this.partsSelected.splice(index, 1);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.label-and-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .tag {
    cursor: pointer;
    &:hover {
      background: gainsboro;
    }
  }
}
</style>